import http from './httpService';
import _ from 'lodash/lang';
import { chunk } from 'lodash/array';

import { BLOCK_CHORDS } from '../utils/chordRhythmPatterns';
import { DEFAULT_INSTRUMENT_ID } from '../utils/midiInstruments';

const apiEndPoint = '/songs';

function url(id) {
  return `${apiEndPoint}/${id}`;
}

function formatSong(song) {
  const songBody = { ...song };

  // When copying songs between teams a song won't have these editor-only properties
  if (songBody.currentPart) {
    delete songBody.currentPart;
    delete songBody.currentLyrics;
    delete songBody.partNumber;
    delete songBody.currentRepeatCount;
  }

  songBody.parts = formatDuplicateParts(songBody);

  return songBody;
}

/**
 * A song as fetched, reduced to the fields a save sends back. youtube is optional and bpm may
 * predate it being required, so both map to '' when undefined.
 */
export const toSongBody = (song) => ({
  _id: song._id,
  title: song.title,
  artist: song.artist,
  key: song.key,
  transposeBy: song.transposeBy || '', // Semitones to auto-transpose
  bpm: song.bpm || '',
  timeSignature: song.timeSignature || '4/4',
  categoryId: song.category._id,
  youtube: song.youtube || '',
  // null clears a default; the API leaves alone one it isn't sent
  chartInstrumentId: song.chartInstrumentId || null,
  chartRhythm: song.chartRhythm || null,
  chartEffects: song.chartEffects || null,
  favorite: song.favorite,
  parts: song.parts,
  teamId: song.team._id,
  isPublic: song.isPublic || false,
  isHidden: song.isHidden || false,
  isDraft: song.isDraft || false,
  isNewSong: song.isNewSong || false,
  isCopy: song.isCopy,
  originalTeam: song.originalTeam,
  themes: song.themes || [],
});

function formatDuplicateParts(song) {
  const { parts } = _.cloneDeep(song);

  const duplicates = parts.filter((p) => p.isDuplicate === true);

  // Empty duplicates — their lyrics/chords are rebuilt from the original on load, so storing
  // a second copy of the MIDI JSON on every duplicate would only bloat the document.
  // Instruments are NOT stripped: a duplicate inherits them when created but owns them
  // afterwards, so its own assignment has to survive the round trip.
  //
  // `chartData`, the chart itself, goes with the lyrics. Its sound and intensity fields stay: each
  // occurrence of a part is arranged on its own, its instrument and rhythm included.
  //
  // `key` is dropped for a different reason than the lyrics: a duplicate never declares one at
  // all. A part's key says the song is in that key from there onward and every part after it
  // inherits, duplicates included — so a duplicate is already in the right key by virtue of where
  // it sits, and storing one on it could only contradict that.
  duplicates.forEach((d) => {
    d.name = '';
    d.number = '';
    d.lyrics = '';
    delete d.key;
    delete d.bpm;
    delete d.midi;
    delete d.chartData;

    // Absent loads as the original's, for songs saved before duplicates kept their own, so one
    // following the song where its original doesn't is saved as the song's
    const original = parts.find((p) => p.id === d.duplicateId);

    if (!d.chartInstrumentId && original?.chartInstrumentId) d.chartInstrumentId = DEFAULT_INSTRUMENT_ID;
    if (!d.chartRhythm && original?.chartRhythm) d.chartRhythm = song.chartRhythm || BLOCK_CHORDS;
  });

  return parts;
}

function loadDuplicateParts(song) {
  const { parts } = song;
  const duplicates = parts.filter((p) => p.isDuplicate === true);

  // A duplicate carries nothing of its own, so one whose original is gone can never render.
  // Reaching into a missing original used to throw and take the whole song down with it.
  const hasOriginal = (d) => parts.some((p) => p.id === d.duplicateId);

  duplicates.filter(hasOriginal).forEach((d) => {
    const originalPart = parts.find((p) => p.id === d.duplicateId);

    d.name = originalPart.name;
    d.number = originalPart.number;
    d.lyrics = originalPart.lyrics;
    d.midi = originalPart.midi;
    d.chartData = originalPart.chartData;
    // A duplicate's own sound, else its original's, as songs saved before it kept one have it
    if (d.chartInstrumentId === undefined) d.chartInstrumentId = originalPart.chartInstrumentId;
    if (d.chartRhythm === undefined) d.chartRhythm = originalPart.chartRhythm;

    // Instruments belong to the duplicate itself, so they're left alone. Songs saved before
    // that change stored no instruments on duplicates at all — those still mirror the original.
    if (d.assignedInstruments === undefined) {
      d.assignedInstruments = originalPart.assignedInstruments;
      d.soloInstruments = originalPart.soloInstruments;
    }

    // `key` is deliberately not restored. A duplicate declares nothing and reads the key in force
    // at its own position, which getPartKeys works out from the parts around it.
  });

  return parts.filter((p) => p.isDuplicate !== true || hasOriginal(p));
}

export async function getSongs() {
  const { data: songs } = await http.get(apiEndPoint);
  return songs;
}

export async function getSong(songId) {
  const { data: song } = await http.get(url(songId));
  return song;
}

// The API's own cap on one batch
export const MAX_BATCH_SONGS = 50;

/** Several of the team's songs in full, a request per MAX_BATCH_SONGS of them */
export async function getSongsBatch(songIds) {
  const batches = chunk(songIds, MAX_BATCH_SONGS);
  const responses = await Promise.all(
    batches.map((ids) => http.get(`${apiEndPoint}/batch`, { params: { ids: ids.join(',') } })),
  );

  return responses.flatMap(({ data }) => data);
}

export async function saveSong(song) {
  const body = formatSong(song);

  // Updating a song
  if (song._id) {
    delete body._id;
    const updatedSong = await http.put(url(song._id), body);
    return updatedSong;
  }

  const newSong = await http.post(apiEndPoint, body);
  return newSong;
}

export function getSearchSongs(title, signal) {
  return http.get(`${apiEndPoint}/searchsongs/${title}`, { signal });
}

export function deleteSong(songId) {
  return http.delete(url(songId));
}

export async function incrementSongPlayCount(songId) {
  const { data: song } = await http.patch(`${url(songId)}/stats/increment`);
  return song;
}

export async function updateSongProperties(songId, properties) {
  const { data: song } = await http.patch(`${url(songId)}/properties`, properties);
  return song;
}

const songService = {
  getSongs,
  getSong,
  getSongsBatch,
  getSearchSongs,
  saveSong,
  toSongBody,
  deleteSong,
  incrementSongPlayCount,
  updateSongProperties,
  loadDuplicateParts,
};

export default songService;
